import { Box, Container, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import ActiveCampaignEmbed from '../components/ActiveCampaignEmbed';
import SectionHeader from '../components/atomic/SectionHeader';
import Footer from '../components/Footer';
import FormErrorBoundary from '../components/FormErrorBoundary';
import Header from '../components/Header';
import ServiceCard from '../components/molecules/ServiceCard';
import { RsmcPillarsSection } from '../components/RsmcPillars';

/**
 * AWS Marketplace listings for the modernization consulting practices.
 * The home page industry cards link to this page; the marketplace listing
 * links now live here.
 */
const AWS_PRACTICES = [
  {
    category: 'Professional Services',
    title: 'SMB',
    description: 'Modernization Consulting for a Small & Medium Sized Business',
    buttonText: 'View in AWS Marketplace',
    link: 'https://aws.amazon.com/marketplace/pp/prodview-2bx5zyiof7lpa?sr=0-6&ref_=beagle&applicationId=AWSMPContessa',
  },
  {
    category: 'Professional Services',
    title: 'Healthcare & Life Sciences',
    description:
      'Modernization consulting for HealthTech, Biotech & Research Organizations',
    buttonText: 'View in AWS Marketplace',
    link: 'https://aws.amazon.com/marketplace/pp/prodview-zxdla5e2bzlty?sr=0-1&ref_=beagle&applicationId=AWSMPContessa',
  },
  {
    category: 'Professional Services',
    title: 'Public Sector',
    description:
      'Modernization consulting for Nonprofits, Credit Unions, Higher Ed, State & Local Government',
    buttonText: 'View in AWS Marketplace',
    link: 'https://aws.amazon.com/marketplace/pp/prodview-lr4rkjigf2tac?sr=0-2&ref_=beagle&applicationId=AWSMPContessa',
  },
];

/**
 * AWS page (rsmc.ai/aws) — landing page for the AWS modernization consulting
 * practices. Reached from the Public Sector, SMB, and Healthcare & Life
 * Sciences "Learn More" cards on the home page.
 *
 * Sections:
 * - Practice cards linking to the AWS Marketplace listings
 * - RSMC pillars (Ready, Secure, Managed, Cloud)
 * - Inline "RSMC Subscriber Sign Up" ActiveCampaign form
 *
 * @returns {JSX.Element} The AWS page layout
 */
function AwsPage() {
  const [formHTML, setFormHTML] = useState('');

  useEffect(() => {
    fetch('/forms/subscribe-form.txt')
      .then((response) => response.text())
      .then((html) => setFormHTML(html))
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error('Error loading subscribe form:', error);
      });
  }, []);

  return (
    <>
      <Header
        scrollToCollective={undefined}
        scrollToServices={undefined}
        scrollToContact={undefined}
      />

      <Box
        sx={{ width: '100%', backgroundColor: '#f8f9fa', minHeight: '100vh' }}
      >
        <SectionHeader
          title="RSMC on AWS"
          subtitle="Modernization consulting powered by AWS fully managed services"
          dark={true}
        />

        {/* Practice cards */}
        <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
          <Typography
            variant="body1"
            sx={{
              color: '#6c757d',
              textAlign: 'center',
              maxWidth: '760px',
              mx: 'auto',
              mb: 5,
              lineHeight: 1.7,
            }}
          >
            Ready Secure Managed Cloud delivers modernization consulting for the
            public sector, small and medium-sized businesses, and healthcare and
            life sciences organizations — available directly through the AWS
            Marketplace.
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, minmax(0, 1fr))',
                md: 'repeat(3, minmax(0, 1fr))',
              },
              gap: 3,
            }}
          >
            {AWS_PRACTICES.map((practice, index) => (
              <ServiceCard
                key={practice.title}
                category={practice.category}
                title={practice.title}
                description={practice.description}
                buttonText={practice.buttonText}
                link={practice.link}
                index={index}
                fullWidth={true}
              />
            ))}
          </Box>
        </Container>

        {/* RSMC pillars */}
        <RsmcPillarsSection />

        {/* RSMC Subscriber Sign Up */}
        <Container maxWidth="md" sx={{ pb: 8 }} style={{ marginTop: '2rem' }}>
          <Box
            sx={{
              backgroundColor: 'var(--color-card)',
              borderRadius: '12px',
              p: { xs: 3, md: 5 },
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              border: '1px solid var(--color-border)',
            }}
          >
            <FormErrorBoundary fallbackMessage="Unable to load subscribe form">
              {formHTML && <ActiveCampaignEmbed htmlContent={formHTML} />}
              {!formHTML && (
                <Typography
                  variant="body2"
                  sx={{
                    color: 'var(--color-text-muted)',
                    textAlign: 'center',
                    py: 4,
                  }}
                >
                  Loading form...
                </Typography>
              )}
            </FormErrorBoundary>
          </Box>
        </Container>
      </Box>

      <Footer />
    </>
  );
}

export default AwsPage;
